import React from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import SEOHead from '@/components/SEOHead';
import { sportPageByPath, SPORT_PAGES } from '@/data/sportPages';
import { softwareApplicationSchema, breadcrumbSchema, faqPageSchema } from '@/lib/seoSchemas';
import { PillarsSection, FreeSchedulerBand } from '@/components/landing/PlatformSections';
import { SportHero, SportFeatures, SportSteps, SportFaq } from '@/components/landing/SportSections';
import { SiteFooter } from '@/components/landing/SiteFooter';

const sportSchema = (page) => ({
  ...softwareApplicationSchema(),
  name: `Schedule Guru for ${page.label}`,
  description: page.seoDescription,
  url: `https://scheduleguru.ws${page.path}`,
});

const Nav = ({ current }) => (
  <header className="glass-effect sticky top-0 z-50 border-b border-slate-200">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between gap-4">
      <Link to="/" data-testid="nav-logo"><img src="/logo-transparent.png" alt="Schedule Guru" className="h-16 w-auto" /></Link>
      <nav className="hidden xl:flex items-center gap-6 text-sm text-slate-600">
        {SPORT_PAGES.map((s) => (
          <Link key={s.path} to={s.path} data-testid={`sport-nav-${s.sport}`} className={s.path === current ? 'text-[#00247D] font-semibold' : 'hover:text-[#00247D] transition-colors'}>{s.label}</Link>
        ))}
        <Link to="/pricing" className="hover:text-[#00247D] transition-colors" data-testid="sport-nav-pricing">Pricing</Link>
      </nav>
      <Link to="/login"><Button variant="outline" className="border-[#00247D] text-[#00247D]" data-testid="nav-login-btn">Sign In</Button></Link>
    </div>
    <nav className="xl:hidden flex flex-wrap items-center gap-x-5 gap-y-1 px-4 pb-3 text-sm text-slate-600" data-testid="sport-mobile-nav">
      {SPORT_PAGES.map((s) => (
        <Link key={s.path} to={s.path} data-testid={`sport-mnav-${s.sport}`} className={`whitespace-nowrap ${s.path === current ? 'text-[#00247D] font-semibold' : ''}`}>{s.label}</Link>
      ))}
      <Link to="/pricing" className="whitespace-nowrap" data-testid="sport-mnav-pricing">Pricing</Link>
    </nav>
  </header>
);

export default function SportLandingPage() {
  const { pathname } = useLocation();
  const page = sportPageByPath(pathname.replace(/\/$/, ''));
  if (!page) return <Navigate to="/" replace />;
  return (
    <div className="min-h-screen bg-white" data-testid={`sport-page-${page.sport}`}>
      <SEOHead
        title={page.seoTitle}
        description={page.seoDescription}
        ogTitle={`${page.label} League Management — Your League. One Platform.`}
        path={page.path}
        image={page.image}
        jsonLd={[sportSchema(page), faqPageSchema(page.faqs), breadcrumbSchema([{ name: 'Home', url: '/' }, { name: `${page.label} League Software`, url: page.path }])]}
      />
      <Nav current={page.path} />
      <SportHero page={page} />
      <SportFeatures page={page} />
      <SportSteps page={page} />
      <FreeSchedulerBand />
      <PillarsSection />
      <SportFaq page={page} />
      <SiteFooter />
    </div>
  );
}
